import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { WalletMinimal } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

import { ChargeItemDefinitionPicker } from "@/components/Common/ChargeItemDefinitionPicker";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ChargeItemDefinitionBase } from "@/types/billing/chargeItemDefinition/chargeItemDefinition";
import { FacilityRead } from "@/types/facility/facility";
import facilityApi from "@/types/facility/facilityApi";
import mutate from "@/Utils/request/mutate";

interface FacilityRegistrationChargeSelectorProps {
  facility: FacilityRead;
}

const registrationChargeSchema = z.object({
  registration_charge_item_definition: z
    .custom<ChargeItemDefinitionBase>()
    .nullable()
    .optional(),
});

type FormValues = z.infer<typeof registrationChargeSchema>;

export default function FacilityRegistrationChargeSelector({
  facility,
}: FacilityRegistrationChargeSelectorProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(registrationChargeSchema),
    defaultValues: {
      registration_charge_item_definition:
        facility.registration_charge_item_definition ?? null,
    },
  });

  const { mutate: setRegistrationChargeItemDefinition, isPending } =
    useMutation({
      mutationFn: mutate(facilityApi.setRegistrationChargeItemDefinition, {
        pathParams: { facilityId: facility.id },
      }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["facility", facility.id] });
        toast.success(
          t("registration_charge_updated_successfully", {
            defaultValue: "Registration charge updated",
          }),
        );
        setIsOpen(false);
      },
    });

  const handleSheetOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      form.reset({
        registration_charge_item_definition:
          facility.registration_charge_item_definition ?? null,
      });
    }
  };

  const onSubmit = (data: FormValues) => {
    setRegistrationChargeItemDefinition({
      registration_charge_item_definition:
        data.registration_charge_item_definition?.slug ?? null,
    });
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleSheetOpenChange}>
      <SheetTrigger asChild>
        <Button
          className="cursor-pointer font-semibold"
          variant="outline"
          size="sm"
        >
          <WalletMinimal className="size-4" />
          {t("manage_charges")}
        </Button>
      </SheetTrigger>

      <SheetContent side="right" className="w-[90%] sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>
            {t("registration_charge", { defaultValue: "Registration charge" })}
          </SheetTitle>
          <SheetDescription>
            {t("select_or_create_registration_charge_item_definition", {
              defaultValue:
                "Select or create the charge item definition to apply automatically when a patient is first registered in this facility.",
            })}
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mt-6 flex flex-col gap-6"
          >
            <FormField
              control={form.control}
              name="registration_charge_item_definition"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("registration_charge_definition", {
                      defaultValue: "Registration charge definition",
                    })}
                  </FormLabel>
                  <FormControl>
                    <div className="mt-2 flex gap-2 flex-col sm:flex-row">
                      <ChargeItemDefinitionPicker
                        facilityId={facility.id}
                        value={field.value ?? undefined}
                        onValueChange={(definition) => {
                          const selected = Array.isArray(definition)
                            ? definition[0]
                            : definition;
                          field.onChange(selected ?? null);
                        }}
                        placeholder={t("select_charge_item_definition")}
                        className="grow"
                        showCreateButton
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-between gap-4 border-t pt-4 sticky bottom-0 bg-white max-sm:flex-col">
              <Button
                variant="outline"
                type="button"
                onClick={() =>
                  setRegistrationChargeItemDefinition({
                    registration_charge_item_definition: null,
                  })
                }
                disabled={
                  isPending || !facility.registration_charge_item_definition
                }
                className="w-full sm:w-auto"
              >
                {t("disable_registration_charge", {
                  defaultValue: "Disable registration charge",
                })}
              </Button>

              <div className="flex gap-4 max-sm:flex-col sm:justify-end">
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="w-full sm:w-auto"
                >
                  {t("cancel")}
                </Button>
                <Button
                  type="submit"
                  className="w-full sm:w-auto"
                  disabled={isPending}
                >
                  {t("save")}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
